import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import { animes } from "@/mocks/animes";

const regions = ["全部", "日本", "中国", "韩国", "欧美"];
const years = ["全部", "2025", "2024", "2023"];
const sortOptions = [
  { value: "popularity", label: "综合热度" },
  { value: "rating", label: "评分最高" },
  { value: "year", label: "最新年份" },
];

const genres = ["全部", ...Array.from(new Set(animes.flatMap((a) => a.genres)))];
const PAGE_SIZE = 8;

interface FilterBarProps {
  genre: string;
  region: string;
  year: string;
  sort: string;
  onGenre: (v: string) => void;
  onRegion: (v: string) => void;
  onYear: (v: string) => void;
  onSort: (v: string) => void;
  onReset: () => void;
}

function FilterBar({ genre, region, year, sort, onGenre, onRegion, onYear, onSort, onReset }: FilterBarProps) {
  const chip = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
      active
        ? "bg-primary-600 text-background-50"
        : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
    }`;

  const group = (title: string, children: ReactNode) => (
    <div className="mb-5 last:mb-0">
      <div className="text-sm font-semibold text-foreground-800 mb-2.5">{title}</div>
      <div className="flex items-center flex-wrap gap-2">{children}</div>
    </div>
  );

  return (
    <div>
      {group(
        "类型",
        genres.map((g) => (
          <button key={g} type="button" onClick={() => onGenre(g)} className={chip(genre === g)}>
            {g}
          </button>
        )),
      )}
      {group(
        "地区",
        regions.map((r) => (
          <button key={r} type="button" onClick={() => onRegion(r)} className={chip(region === r)}>
            {r}
          </button>
        )),
      )}
      {group(
        "年份",
        years.map((y) => (
          <button key={y} type="button" onClick={() => onYear(y)} className={chip(year === y)}>
            {y}
          </button>
        )),
      )}
      {group(
        "排序",
        sortOptions.map((s) => (
          <button key={s.value} type="button" onClick={() => onSort(s.value)} className={chip(sort === s.value)}>
            {s.label}
          </button>
        )),
      )}
      <button
        type="button"
        onClick={onReset}
        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-refresh-line"></i>
        </span>
        重置筛选
      </button>
    </div>
  );
}

export default function AnimeListPage() {
  const [searchParams] = useSearchParams();
  const genreParam = searchParams.get("genre");
  const regionParam = searchParams.get("region");
  const [genre, setGenre] = useState(genreParam || "全部");
  const [region, setRegion] = useState(regionParam || "全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("popularity");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setGenre(genreParam || "全部");
    setRegion(regionParam || "全部");
    setVisible(PAGE_SIZE);
  }, [genreParam, regionParam]);

  // lock body scroll while mobile filter panel is open
  useEffect(() => {
    document.body.style.overflow = panelOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [panelOpen]);

  const filtered = useMemo(() => {
    const list = animes.filter(
      (a) =>
        (genre === "全部" || a.genres.includes(genre)) &&
        (region === "全部" || a.region === region) &&
        (year === "全部" || String(a.year) === year),
    );
    return [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "year") return b.year - a.year;
      return b.popularity - a.popularity;
    });
  }, [genre, region, year, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const update = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("popularity");
    setVisible(PAGE_SIZE);
  };

  const filterProps: FilterBarProps = {
    genre,
    region,
    year,
    sort,
    onGenre: update(setGenre),
    onRegion: update(setRegion),
    onYear: update(setYear),
    onSort: update(setSort),
    onReset: resetFilters,
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "动漫作品" }]} />

        <div className="mt-5 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">动漫作品库</h1>
          <p className="text-sm text-foreground-500 leading-relaxed">
            按类型、地区与年份筛选你感兴趣的作品，丰富的动漫资源一站浏览。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          <aside className="hidden lg:block">
            <div className="sticky top-20 rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-4">筛选条件</h2>
              <FilterBar {...filterProps} />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-bold text-primary-700">{filtered.length}</span> 部作品
              </p>
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 border border-background-300 text-sm font-medium text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line"></i>
                </span>
                筛选
              </button>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {shown.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-1.5 px-6 h-11 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      加载更多
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-down-line"></i>
                      </span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 flex flex-col items-center justify-center text-center">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <p className="text-base font-medium text-foreground-800 mb-1">没有符合条件的作品</p>
                <p className="text-sm text-foreground-500 mb-5">试试放宽筛选条件，发现更多作品</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-5 h-10 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile filter panel */}
      <div
        className={`lg:hidden fixed inset-0 z-50 ${panelOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!panelOpen}
      >
        <div
          className={`absolute inset-0 bg-primary-950/50 transition-opacity ${
            panelOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setPanelOpen(false)}
        ></div>
        <div
          className={`absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 transition-transform duration-300 ${
            panelOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold text-foreground-950">筛选条件</h2>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setPanelOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <FilterBar {...filterProps} />
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            className="mt-6 w-full h-11 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors cursor-pointer"
          >
            查看 {filtered.length} 部作品
          </button>
        </div>
      </div>
    </div>
  );
}